import React, { useState, useEffect, useCallback, useRef } from "react";
import { useFashnTryOn } from "../hooks/useFashnTryOn";
import { UserUpload, useUserUploads } from "../hooks/useUserUploads";
import { DiscoverImage } from "../hooks/useDiscoverImages";
import {
	TryOnOutfitUpload,
	useTryOnOutfitUploads,
} from "../hooks/useTryOnOutfitUploads";
import { Upload, ChevronsUpDown } from "lucide-react";
import ImageUploader from "../components/ImageUploader";
import ImageCarousel from "../components/ImageCarousel";

// Our wrapper component that will handle remounting
interface TryOnContainerProps {
	discoverImages: DiscoverImage[];
	initialSelectedImage?: DiscoverImage;
	initialSelectedPhoto?: UserUpload;
	fromDiscover?: boolean;
}

const TryOnContainer: React.FC<TryOnContainerProps> = ({
	discoverImages,
	initialSelectedImage,
	initialSelectedPhoto,
	fromDiscover,
}) => {
	// A key state to force remount
	const [mountKey, setMountKey] = useState(0);

	// Function to handle reset
	const handleReset = () => {
		setMountKey((prev) => prev + 1);
	};

	return (
		<InnerVirtualTryOn
			key={`tryOn-${mountKey}`}
			discoverImages={discoverImages}
			onReset={handleReset}
			initialSelectedImage={initialSelectedImage}
			initialSelectedPhoto={initialSelectedPhoto}
			fromDiscover={fromDiscover}
		/>
	);
};

// Define view types and garment source types
type ViewType = "selection" | "loading" | "result";
type GarmentSource = "discover" | "uploads";

// Props for the inner component
interface InnerVirtualTryOnProps {
	discoverImages: DiscoverImage[];
	onReset: () => void;
	initialSelectedImage?: DiscoverImage;
	initialSelectedPhoto?: UserUpload;
	fromDiscover?: boolean;
}

// The actual component implementation, now as an inner component
const InnerVirtualTryOn: React.FC<InnerVirtualTryOnProps> = ({
	discoverImages,
	onReset,
	initialSelectedImage,
	initialSelectedPhoto,
	fromDiscover,
}) => {
	const { uploads: userUploads, loading: userUploadsLoading, refreshUploads } =
		useUserUploads();
	const {
		outfits: tryOnOutfits,
		loading: outfitsLoading,
		error: outfitsError,
		uploadOutfit,
		deleteOutfit,
		refreshOutfits,
	} = useTryOnOutfitUploads();
	const { loading, error, errorType, result, progress, tryOn } = useFashnTryOn();

	const [selectedModel, setSelectedModel] = useState<UserUpload | null>(
		initialSelectedPhoto || null
	);
	const [selectedGarment, setSelectedGarment] = useState<DiscoverImage | null>(
		initialSelectedImage || null,
	);
	const [selectedOutfit, setSelectedOutfit] =
		useState<TryOnOutfitUpload | null>(null);
	const [previewOutfit, setPreviewOutfit] = useState<TryOnOutfitUpload | null>(null);
	const [garmentSource, setGarmentSource] = useState<GarmentSource>(
		fromDiscover ? "discover" : "uploads",
	);
	const [currentView, setCurrentView] = useState<ViewType>("selection");
	const [uploadingOutfit, setUploadingOutfit] = useState(false);
	const [showUploader, setShowUploader] = useState(false);
	const [columns, setColumns] = useState<UserUpload[][]>([]);
	const [columnCount, setColumnCount] = useState(2);
	const [discoverColumns, setDiscoverColumns] = useState<DiscoverImage[][]>([]);
	const [discoverColumnCount] = useState(2);
	const [outfitColumns, setOutfitColumns] = useState<TryOnOutfitUpload[][]>([]);
	const [outfitColumnCount] = useState(2);

	// Refs for preserving scroll position in scrollable containers
	const leftColumnScrollRef = useRef<HTMLDivElement>(null);
	const rightColumnScrollRef = useRef<HTMLDivElement>(null);
	
	// Preserve scroll position when selecting items
	const preserveScroll = (ref: React.RefObject<HTMLDivElement>) => {
		if (ref.current) {
			const scrollPosition = ref.current.scrollTop;
			setTimeout(() => {
				if (ref.current) {
					ref.current.scrollTop = scrollPosition;
				}
			}, 0);
		}
	};

	// Update the view based on loading and result state
	useEffect(() => {
		if (loading) {
			setCurrentView("loading");
		} else if (result) {
			setCurrentView("result");
		}
	}, [loading, result]);

	// Handle toggle uploader modal
	const toggleUploader = () => {
		setShowUploader(!showUploader);
	};

	// Handle successful upload
	const handleUploadSuccess = () => {
		setShowUploader(false);
		// Force a refresh of the user uploads
		refreshUploads();
	};

	// Handle try-on
	const handleTryOn = async () => {
		if (!selectedModel) return;

		let garmentUrl = "";
		if (garmentSource === "discover" && selectedGarment) {
			garmentUrl = selectedGarment.url;
		} else if (garmentSource === "uploads" && selectedOutfit) {
			garmentUrl = selectedOutfit.url;
		} else {
			return; // No garment selected
		}

		try {
			await tryOn(selectedModel.url, garmentUrl);
		} catch (err) {
			console.error("Try-on error:", err);
		}
	};

	// Try another - this will trigger a complete remount via the container
	const handleTryAnother = () => {
		onReset();
	};

	// Handle outfit upload
	const handleOutfitUpload = async (file: File) => {
		setUploadingOutfit(true);
		try {
			const result = await uploadOutfit(file);
			if (result) {
				setSelectedOutfit(result);
				setGarmentSource("uploads");
				// Clear any previously selected discover garment
				setSelectedGarment(null);
			}
		} catch (error) {
			console.error("Error uploading outfit:", error);
		} finally {
			setUploadingOutfit(false);
		}
	};

	// Handle refresh after upload
	const handleRefreshAfterUpload = () => {
		refreshOutfits();
	};

	// Handle image loading error
	const handleImageError = (
		event: React.SyntheticEvent<HTMLImageElement, Event>,
		source: string,
	) => {
		console.error(`Failed to load ${source} image:`, event);

		// Show an error indicator on the image
		const target = event.target as HTMLImageElement;

		// Set a placeholder error image
		target.src =
			"data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNmOWY5ZjkiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1zaXplPSIxNCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgYWxpZ25tZW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5OTk5OTkiPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg==";
	};

	// Custom file upload handler for the try-on outfits
	const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			await handleOutfitUpload(e.target.files[0]);
			handleRefreshAfterUpload();
		}
	};

	// Handle delete outfit
	const handleDeleteOutfit = async (outfit: TryOnOutfitUpload) => {
		if (window.confirm(`Are you sure you want to delete ${outfit.filename}?`)) {
			try {
				const success = await deleteOutfit(outfit.id);
				
				if (success) {
					// If the delete was successful and we have this outfit selected
					if (selectedOutfit?.id === outfit.id) {
						setSelectedOutfit(null);
					}
					
					// If we're previewing this outfit, close the preview
					if (previewOutfit?.id === outfit.id) {
						setPreviewOutfit(null);
					}
					
					// Refresh outfits after deletion
					refreshOutfits();
				} else {
					console.error("Failed to delete outfit:", outfit.id);
					alert("Failed to delete outfit. Please try again.");
				}
			} catch (err) {
				console.error("Error deleting outfit:", err);
				alert("An error occurred while deleting the outfit.");
			}
		}
	};
	
	// Handle view outfit in modal
	const handleViewOutfit = (outfit: TryOnOutfitUpload) => {
		setPreviewOutfit(outfit);
	};
	
	// Close preview modal
	const closePreviewModal = () => {
		setPreviewOutfit(null);
	};

	// Create columns for masonry layout based on image aspect ratios - user photos
	const distributeImagesIntoColumns = useCallback(() => {
		if (userUploads.length === 0) return;
		
		// Create empty columns
		const newColumns: UserUpload[][] = Array.from(
			{ length: columnCount },
			() => [],
		);
		
		// Track the total "height" of each column
		const columnHeights = Array(columnCount).fill(0);
		
		// Distribute images by always adding to the shortest column
		userUploads.forEach((upload) => {
			// Find the shortest column
			const shortestColumnIndex = columnHeights.indexOf(
				Math.min(...columnHeights),
			);
			
			// Add image to that column
			newColumns[shortestColumnIndex].push(upload);
			
			// Update the column height - assume square aspect ratio if unknown
			columnHeights[shortestColumnIndex] += 1;
		});
		
		setColumns(newColumns);
	}, [userUploads, columnCount]);
	
	// Create columns for masonry layout - discover images
	const distributeDiscoverImagesIntoColumns = useCallback(() => {
		if (discoverImages.length === 0 && !initialSelectedImage) return;
		
		// Determine column count based on screen width
		const width = window.innerWidth;
		const useColumns = width < 640 ? 1 : discoverColumnCount;
		
		// Create empty columns
		const newColumns: DiscoverImage[][] = Array.from(
			{ length: useColumns },
			() => [],
		);
		
		// If we came from the discover page and have an initialSelectedImage,
		// place it at the top of the first column
		if (fromDiscover && initialSelectedImage) {
			newColumns[0].push(initialSelectedImage);
		}
		
		// Track the total "height" of each column, accounting for the initialSelectedImage
		const columnHeights = Array(useColumns).fill(0);
		if (fromDiscover && initialSelectedImage) {
			// Add the height of the initialSelectedImage to the first column
			columnHeights[0] = 1 / (initialSelectedImage.aspectRatio || 1);
		}
		
		// Filter out the initialSelectedImage from the distribution list to avoid duplication
		const imagesToDistribute = fromDiscover && initialSelectedImage
			? discoverImages.filter(img => img.id !== initialSelectedImage.id)
			: discoverImages;
		
		// Distribute the rest of the images by always adding to the shortest column
		imagesToDistribute.forEach((image) => {
			// Find the shortest column
			const shortestColumnIndex = columnHeights.indexOf(
				Math.min(...columnHeights),
			);
			
			// Add image to that column
			newColumns[shortestColumnIndex].push(image);
			
			// Update the column height using the aspect ratio if available
			// or assume square aspect ratio if unknown
			columnHeights[shortestColumnIndex] += 1 / (image.aspectRatio || 1);
		});
		
		setDiscoverColumns(newColumns);
	}, [discoverImages, initialSelectedImage, fromDiscover, discoverColumnCount]);
	
	// Create columns for masonry layout - my uploads
	const distributeOutfitsIntoColumns = useCallback(() => {
		if (tryOnOutfits.length === 0) return;
		
		// Determine column count based on screen width
		const width = window.innerWidth;
		const useColumns = width < 640 ? 1 : outfitColumnCount;
		
		// Create empty columns
		const newColumns: TryOnOutfitUpload[][] = Array.from(
			{ length: useColumns },
			() => [],
		);
		
		// Track the total "height" of each column
		const columnHeights = Array(useColumns).fill(0);
		
		// Distribute images by always adding to the shortest column
		tryOnOutfits.forEach((outfit) => {
			// Find the shortest column
			const shortestColumnIndex = columnHeights.indexOf(
				Math.min(...columnHeights),
			);
			
			// Add image to that column
			newColumns[shortestColumnIndex].push(outfit);
			
			// Update the column height - assume square aspect ratio
			columnHeights[shortestColumnIndex] += 1;
		});
		
		setOutfitColumns(newColumns);
	}, [tryOnOutfits, outfitColumnCount]);

	// Update columns when images change
	useEffect(() => {
		distributeImagesIntoColumns();
	}, [distributeImagesIntoColumns]);

	// Update discover columns when discover images change
	useEffect(() => {
		distributeDiscoverImagesIntoColumns();
	}, [distributeDiscoverImagesIntoColumns]);

	// Update outfit columns when my uploads change
	useEffect(() => {
		distributeOutfitsIntoColumns();
	}, [distributeOutfitsIntoColumns]);

	// Update columns when window resizes
	useEffect(() => {
		const handleResize = () => {
			const width = window.innerWidth;
			
			// For try-on page columns, use responsive column count
			if (width < 640) { // Small mobile screens
				setColumnCount(1);
			} else {
				setColumnCount(2);
			}
		};
		
		handleResize(); // Initial call
		window.addEventListener("resize", handleResize);
		
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	// Selection View Component with Two-Column Layout
	const SelectionView = () => {
		// Is empty state
		const isEmptyState = userUploads.length === 0 && !userUploadsLoading;
		
		// Create the upload modal
		const uploadModal = showUploader && (
			<div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-haus-light-black bg-opacity-50" onClick={() => setShowUploader(false)}>
				<div className="bg-haus-light-white rounded-lg p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
					<h3 className="text-lg font-medium mb-4">Upload a Photo</h3>
					<ImageUploader onUploadSuccess={handleUploadSuccess} />
					<div className="flex justify-end mt-4">
						<button 
							onClick={() => setShowUploader(false)}
							className="px-3 py-1.5 border border-haus-dark-green bg-transparent text-haus-dark-green text-sm rounded hover:bg-haus-light-olive hover:text-haus-light-white hover:border-haus-light-olive transition-colors"
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		);
		
		return (
			<div className="-mx-4 sm:mx-0">
				{outfitsError && (
					<div className="mb-4 p-3 text-red-700 border border-red-300 text-sm rounded-md bg-red-50">
						{outfitsError}
					</div>
				)}

				{/* Two-column layout - always side by side, even on mobile */}
				<div className="grid grid-cols-2 gap-2 sm:gap-4 px-2 sm:px-0">
					{/* Left Column - My Photos */}
					<div className="h-[70vh] rounded-lg overflow-hidden flex flex-col md:border-2 md:border-gray-300">
						{/* Header with upload button when not empty */}
						<div className="p-2 sm:p-3 md:p-4 flex justify-between items-center md:border-b md:border-gray-300">
							<h3 className="text-sm sm:text-base md:text-lg font-medium">Photo</h3>
							{!isEmptyState && (
								<button
									onClick={toggleUploader}
									className="flex items-center justify-center p-1 sm:p-1.5 bg-haus-dark-green text-haus-light-white rounded hover:bg-haus-light-olive transition-colors"
								>
									<Upload size={14} className="sm:hidden" />
									<Upload size={16} className="hidden sm:block" />
								</button>
							)}
						</div>
						
						<div className="flex-1 overflow-y-auto p-2 sm:p-3 md:p-4" ref={leftColumnScrollRef}>
							{userUploadsLoading ? (
								<div className="flex justify-center items-center h-full">
									<div className="spinner"></div>
								</div>
							) : isEmptyState ? (
								<div className="py-16 flex flex-col items-center justify-center text-center border-2 border-dashed border-gray-200 rounded-lg h-full">
									<h3 className="text-lg font-medium text-gray-800 mb-2">No Photos Yet</h3>
									<p className="text-gray-500 text-sm max-w-md mb-6">
										Upload photos of yourself to try on different outfits using our virtual try-on feature.
									</p>
									<button
										onClick={toggleUploader}
										className="flex items-center gap-2 px-4 py-2 bg-haus-dark-green text-haus-light-white rounded hover:bg-haus-light-olive transition-colors"
									>
										<Upload size={16} />
										<span className="text-sm font-medium">Upload Your First Photo</span>
									</button>
								</div>
							) : (
								<div className="flex gap-2 xs:gap-3 sm:gap-4 w-full">
									{columns.map((column, columnIndex) => (
										<div
											key={columnIndex}
											className="flex-1 flex flex-col gap-2 xs:gap-3 sm:gap-4"
										>
											{column.map((upload) => (
												<div
													key={upload.id}
													className={`relative group cursor-pointer overflow-hidden rounded-lg ${
														selectedModel?.id === upload.id
															? "ring-2 ring-haus-dark-green"
															: ""
													}`}
													onClick={() => {
														// Toggle selection - unselect if clicking the same image
														if (selectedModel?.id === upload.id) {
															setSelectedModel(null);
														} else {
															setSelectedModel(upload);
															preserveScroll(leftColumnScrollRef);
														}
													}}
												>
													<img
														src={upload.url}
														alt={upload.filename}
														className="w-full h-auto object-cover transition-transform group-hover:scale-105"
														onError={(e) => handleImageError(e, "model")}
													/>
													
													{/* Selection indicator */}
													{selectedModel?.id === upload.id && (
														<div className="absolute top-2 right-2 bg-haus-dark-green text-haus-light-white rounded-full p-1">
															<svg
																xmlns="http://www.w3.org/2000/svg"
																className="h-4 w-4"
																viewBox="0 0 20 20"
																fill="currentColor"
															>
																<path
																	fillRule="evenodd"
																	d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
																	clipRule="evenodd"
																/>
															</svg>
														</div>
													)}
													
													{/* Hover overlay */}
													<div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
														<div className="flex space-x-1 sm:space-x-2">
															<button
																onClick={(e) => {
																	e.stopPropagation();
																	setSelectedModel(upload);
																	preserveScroll(leftColumnScrollRef);
																}}
																className="p-1 sm:p-1.5 bg-white rounded-full text-black hover:bg-gray-100"
																title="Select Photo"
															>
																<svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" viewBox="0 0 20 20" fill="currentColor">
																	<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
																</svg>
															</button>
														</div>
													</div>
												</div>
											))}
										</div>
									))}
								</div>
							)}
						</div>
					</div>

					{/* Right Column - Outfits (Tabbed) */}
					<div className="h-[70vh] rounded-lg overflow-hidden flex flex-col md:border-2 md:border-gray-300">
						{/* Header */}
						<div className="p-2 sm:p-3 md:p-4 flex justify-between items-center md:border-b md:border-gray-300">
							<h3 
								onClick={() => setGarmentSource(garmentSource === "discover" ? "uploads" : "discover")}
								className="text-sm sm:text-base md:text-lg font-medium cursor-pointer bg-transparent"
								aria-label={`Switch to ${garmentSource === "discover" ? "uploads" : "discover"}`}
							>
								{garmentSource === "discover" ? "Discover" : "Upload"}
								<ChevronsUpDown 
									className="h-4 w-4 ml-1 inline-block align-middle text-haus-dark-green" 
									strokeWidth={3}
								/>
							</h3>
							{garmentSource === "uploads" && tryOnOutfits.length > 0 && !outfitsLoading && !outfitsError && (
								<label htmlFor="outfit-upload" className="flex items-center justify-center p-1 sm:p-1.5 bg-haus-dark-green text-haus-light-white rounded hover:bg-haus-light-olive transition-colors cursor-pointer">
									<Upload size={14} className="sm:hidden" />
									<Upload size={16} className="hidden sm:block" />
									<input
										type="file"
										id="outfit-upload"
										className="hidden"
										accept="image/jpeg,image/png,image/webp"
										onChange={handleFileUpload}
										disabled={uploadingOutfit}
									/>
								</label>
							)}
						</div>
						
						{/* Tab content - scrollable area */}
						<div className="flex-1 overflow-y-auto p-2 sm:p-3 md:p-4" ref={rightColumnScrollRef}>
							{garmentSource === "discover" ? (
								// Discover Images
								discoverImages.length === 0 ? (
									<div className="py-16 flex flex-col items-center justify-center text-center border-2 border-dashed border-gray-200 rounded-lg h-full">
										<h3 className="text-lg font-medium text-gray-800 mb-2">No Looks Available</h3>
										<p className="text-gray-500 text-sm max-w-md mb-6">
											No discover looks are currently available. Check back later or try uploading your own outfits.
										</p>
										<button
											onClick={() => setGarmentSource("uploads")}
											className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded hover:bg-gray-900 transition-colors"
										>
											<Upload size={16} />
											<span className="text-sm font-medium">Switch to My Uploads</span>
										</button>
									</div>
								) : (
									<div className="flex gap-2 xs:gap-3 sm:gap-4 w-full">
										{discoverColumns.map((column, columnIndex) => (
											<div
												key={columnIndex}
												className="flex-1 flex flex-col gap-2 xs:gap-3 sm:gap-4"
											>
												{column.map((image) => (
													<div
														key={image.id}
														className={`relative group cursor-pointer overflow-hidden rounded-lg ${
															selectedGarment?.id === image.id
																? "ring-2 ring-haus-dark-green"
																: ""
														}`}
														onClick={() => {
															// Toggle selection - unselect if clicking the same image
															if (selectedGarment?.id === image.id) {
																setSelectedGarment(null);
															} else {
																setSelectedGarment(image);
																setSelectedOutfit(null); // Clear any selected upload outfit
																preserveScroll(rightColumnScrollRef);
															}
														}}
													>
														<img
															src={image.url}
															alt="Outfit"
															className="w-full h-auto object-cover transition-transform group-hover:scale-105"
															onError={(e) => handleImageError(e, "discover")}
														/>

														{selectedGarment?.id === image.id && (
															<div className="absolute top-2 right-2 bg-haus-dark-green text-haus-light-white rounded-full p-1">
																<svg
																	xmlns="http://www.w3.org/2000/svg"
																	className="h-4 w-4"
																	viewBox="0 0 20 20"
																	fill="currentColor"
																>
																	<path
																		fillRule="evenodd"
																		d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
																		clipRule="evenodd"
																	/>
																</svg>
															</div>
														)}
														
														{/* Hover overlay */}
														<div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
															<div className="flex space-x-2">
																<button
																	onClick={(e) => {
																		e.stopPropagation();
																		setSelectedGarment(image);
																		preserveScroll(rightColumnScrollRef);
																	}}
																	className="p-1.5 bg-white rounded-full text-black hover:bg-gray-100"
																	title="Select Look"
																>
																	<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
																		<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
																	</svg>
																</button>
															</div>
														</div>
													</div>
												))}
											</div>
										))}
									</div>
								)
							) : (
								// My Uploads Tab
								<div>
									{/* Only show upload progress if actively uploading */}
									{uploadingOutfit && (
										<div className="mb-4 p-3 border border-gray-200 rounded-lg">
											<h4 className="text-sm font-medium text-gray-700 mb-2">
												Uploading outfit...
											</h4>
											<div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
												<div
													className="bg-black h-1.5"
													style={{ width: "100%" }}
												></div>
											</div>
										</div>
									)}

									{outfitsLoading ? (
										<div className="py-6 text-center">
											<div className="spinner mx-auto mb-2"></div>
											<p className="text-gray-500 text-sm">Loading your uploaded outfits...</p>
										</div>
									) : outfitsError ? (
										<div className="py-6 text-center">
											<p className="text-red-500 text-sm">{outfitsError}</p>
										</div>
									) : tryOnOutfits.length === 0 ? (
										<div className="py-16 flex flex-col items-center justify-center text-center border-2 border-dashed border-gray-200 rounded-lg h-full">
											<h3 className="text-lg font-medium text-gray-800 mb-2">No Outfits Yet</h3>
											<p className="text-gray-500 text-sm max-w-md mb-6">
												Upload outfit images that you'd like to try on with our virtual try-on feature.
											</p>
											<button
												onClick={() => document.getElementById("outfit-upload")?.click()}
												className="flex items-center gap-2 px-4 py-2 bg-haus-dark-green text-haus-light-white rounded hover:bg-haus-light-olive transition-colors"
											>
												<Upload size={16} />
												<span className="text-sm font-medium">Upload</span>
											</button>
											{/* Hidden file input */}
											<input
												type="file"
												id="outfit-upload"
												className="hidden"
												accept="image/jpeg,image/png,image/webp"
												onChange={handleFileUpload}
												disabled={uploadingOutfit}
											/>
										</div>
									) : (
										<div className="flex gap-2 xs:gap-3 sm:gap-4 w-full">
											{outfitColumns.map((column, columnIndex) => (
												<div
													key={columnIndex}
													className="flex-1 flex flex-col gap-2 xs:gap-3 sm:gap-4"
												>
													{column.map((outfit) => (
														<div
															key={outfit.id}
															className={`relative group cursor-pointer overflow-hidden rounded-lg ${
																selectedOutfit?.id === outfit.id
																	? "ring-2 ring-haus-dark-green"
																	: ""
															}`}
															onClick={() => {
																// Toggle selection - unselect if clicking the same image
																if (selectedOutfit?.id === outfit.id) {
																	setSelectedOutfit(null);
																} else {
																	setSelectedOutfit(outfit);
																	setSelectedGarment(null); // Clear any selected discover garment
																	preserveScroll(rightColumnScrollRef);
																}
															}}
														>
															<img
																src={outfit.url}
																alt={outfit.filename}
																className="w-full h-auto object-cover transition-transform group-hover:scale-105"
																onError={(e) => handleImageError(e, "uploads")}
															/>

															{selectedOutfit?.id === outfit.id && (
																<div className="absolute top-2 right-2 bg-haus-dark-green text-haus-light-white rounded-full p-1">
																	<svg
																		xmlns="http://www.w3.org/2000/svg"
																		className="h-4 w-4"
																		viewBox="0 0 20 20"
																		fill="currentColor"
																	>
																		<path
																			fillRule="evenodd"
																			d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
																			clipRule="evenodd"
																		/>
																	</svg>
																</div>
															)}
															
															{/* Hover overlay with view and delete buttons */}
															<div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
																<div className="flex space-x-2">
																	<button
																		onClick={(e) => {
																			e.stopPropagation();
																			handleViewOutfit(outfit);
																		}}
																		className="p-1.5 bg-white rounded-full text-gray-700 hover:bg-gray-100"
																		title="View Image"
																	>
																		<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
																			<path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
																			<path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
																		</svg>
																	</button>
																	
																	<button
																		onClick={(e) => {
																			e.stopPropagation();
																			handleDeleteOutfit(outfit);
																		}}
																		className="p-1.5 bg-white rounded-full text-red-600 hover:bg-red-50"
																		title="Delete Image"
																	>
																		<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
																			<path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
																		</svg>
																	</button>
																</div>
															</div>
														</div>
													))}
												</div>
											))}
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Floating Try On Button */}
				<div className="fixed md:static bottom-6 left-1/2 md:left-auto transform -translate-x-1/2 md:transform-none md:mt-6 md:flex md:justify-center z-10">
					<button
						onClick={handleTryOn}
						disabled={
							!selectedModel ||
							!(selectedGarment || selectedOutfit) ||
							uploadingOutfit
						}
						className={`px-6 py-3 rounded font-medium text-base shadow-lg transition-colors duration-200 ${
							!selectedModel ||
							!(selectedGarment || selectedOutfit) ||
							uploadingOutfit
								? "bg-gray-200 text-gray-500 cursor-not-allowed pointer-events-none"
								: "bg-haus-dark-green text-haus-light-white hover:bg-haus-light-olive"
						}`}
					>
						{uploadingOutfit ? "Uploading..." : "Try Outfit"}
					</button>
				</div>
				
				{/* Upload modal */}
				{uploadModal}
			</div>
		);
	};

	// Loading View Component
	const LoadingView = () => (
		<div className="space-y-4 sm:space-y-6">
			<div className="border-2 border-gray-300 rounded-lg text-center py-8 sm:py-12 bg-transparent">
				<div className="spinner mx-auto mb-4 sm:mb-5"></div>
				<h3 className="text-base sm:text-lg font-medium mb-1 sm:mb-2">
					Creating your try-on...
				</h3>
				<p className="text-gray-600 text-sm sm:text-base mb-4 sm:mb-5">
					This usually takes 10-15 seconds
				</p>
				<div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-2 sm:h-2.5">
					<div
						className="bg-black h-2 sm:h-2.5 rounded-full transition-all duration-300 ease-out"
						style={{ width: `${progress}%` }}
					/>
				</div>
			</div>
		</div>
	);

	// Result View Component
	const ResultView = () => {
		// Create carousel items
		const carouselItems = [
			{
				id: 'result',
				imageUrl: result || '',
				title: 'Your New Look',
				alt: 'Virtual try-on result'
			},
			{
				id: 'garment',
				imageUrl: garmentSource === "discover" 
					? selectedGarment?.url || '' 
					: selectedOutfit?.url || '',
				title: 'Selected Look',
				alt: 'Selected outfit'
			},
			{
				id: 'original',
				imageUrl: selectedModel?.url || '',
				title: 'Original Photo',
				alt: 'Your original photo'
			}
		].filter(item => item.imageUrl); // Remove any items without an image URL
		
		return (
			<div className="space-y-4 sm:space-y-6 flex flex-col items-center">
				{error ? (
					<div className="p-4 sm:p-6 border border-red-300 rounded-lg bg-red-50 text-red-600 border-l-4 border-l-red-400 w-full max-w-2xl mx-auto">
						<h3 className="text-base sm:text-lg font-medium mb-2">
							Error Creating Try-On
						</h3>
						<p className="mb-4 text-sm sm:text-base">{error}</p>
						<div className="flex space-x-3 sm:space-x-4">
							<button
								onClick={onReset}
								className="px-4 sm:px-5 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors text-sm sm:text-base"
							>
								Try Again
							</button>
							<button
								onClick={handleTryAnother}
								className="px-4 sm:px-5 py-2 bg-haus-dark-green text-haus-light-white rounded hover:bg-haus-light-olive transition-colors text-sm sm:text-base"
							>
								Try Different Images
							</button>
						</div>
					</div>
				) : (
					<>
						<div className="w-full max-w-3xl mx-auto">
							<ImageCarousel 
								items={carouselItems} 
								maxHeight="60vh"
								className="w-full"
							/>
						</div>
						
						{/* Floating button at bottom */}
						<div className="flex justify-center mt-8">
							<button
								onClick={handleTryAnother}
								className="px-6 py-3 bg-haus-dark-green text-haus-light-white rounded hover:bg-haus-light-olive transition-colors text-sm sm:text-base flex items-center gap-2"
							>
								Try Different Images
							</button>
						</div>
					</>
				)}
			</div>
		);
	};

	// Render the appropriate view based on state
	return (
		<>
			{currentView === "loading" ? (
				<LoadingView />
			) : currentView === "result" ? (
				<ResultView />
			) : (
				<SelectionView />
			)}
			
			{/* Image preview modal for outfits */}
			{previewOutfit && (
				<div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-75" onClick={closePreviewModal}>
					<div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
						<div className="absolute top-1 right-1 sm:top-2 sm:right-2 z-10">
							<button 
								onClick={closePreviewModal}
								className="p-1 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-75"
							>
								<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>
						
						<div className="bg-white rounded-lg overflow-hidden border-2 border-gray-300">
							<img 
								src={previewOutfit.url} 
								alt={previewOutfit.filename}
								className="w-full h-auto max-h-[70vh] sm:max-h-[80vh] object-contain"
							/>
							
							<div className="p-3 sm:p-4 border-t border-gray-200">
								<h3 className="font-medium text-gray-800 truncate text-sm sm:text-base">{previewOutfit.filename}</h3>
								<div className="flex justify-between text-xs sm:text-sm text-gray-500 mt-1">
									<span>
										{new Date(previewOutfit.createdAt).toLocaleDateString()}
									</span>
									<span>
										{Math.round(previewOutfit.size / 1024)} KB
									</span>
								</div>
								
								<div className="flex justify-between mt-2 sm:mt-3">
									<button
										onClick={(e) => {
											setSelectedOutfit(previewOutfit);
											setSelectedGarment(null);
											closePreviewModal();
										}}
										className="px-2 sm:px-3 py-1 bg-gray-100 text-black rounded hover:bg-gray-200 text-xs sm:text-sm font-medium"
									>
										Select for Try-On
									</button>
									
									<button
										onClick={() => handleDeleteOutfit(previewOutfit)}
										className="px-2 sm:px-3 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200 text-xs sm:text-sm font-medium"
									>
										Delete
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default TryOnContainer;
